import { z } from 'zod'
import QuestionWrapper from '../../questionnaire/formSteps/QuestionWrapper'
import { createStepFamily } from '../../utils/stepFamily'
import type { FormStepResult } from '../../types'
import type { StepFamily } from '../../utils/stepFamily'

const baseAddressComponentProps = {
  title: "What's your address?",
  label: 'Address',
  tooltip: 'We use your address to estimate your usage to save you money on your energy bill.',
  tracking: {
    stepName: 'Address Entry',
    deepStepNumber: 1,
    quickStepNumber: 1,
    deepStepId: '1',
    quickStepId: '1',
  },
}

const addressFieldSchema = z.object({
  street_line: z.string().min(3, 'Address must be at least 3 characters'),
  secondary: z.string().optional(),
  city: z.string().min(3, 'City must be at least 3 characters'),
  state: z.string().min(2, 'State must be at least 2 characters'),
  zipcode: z.string().min(5, 'Zip code must be at least 5 characters'),
  enriched: z.boolean().optional().default(false),
})

export const addressAndMover = {
  component: QuestionWrapper,
  componentProps: {
    ...baseAddressComponentProps,
    movingFieldKey: 'moving' as const,
  },
  name: 'addressAndMover',
  coreIf: true,
  schema: z.object({
    address: addressFieldSchema,
    moving: z.string().min(2, 'Please select one of the options above.'),
  }),
}

export const addressOnly = {
  component: QuestionWrapper,
  componentProps: {
    ...baseAddressComponentProps,
    movingFieldKey: null as null,
  },
  name: 'addressOnly',
  coreIf: true,
  schema: z.object({
    address: addressFieldSchema,
  }),
}

export const addressAndMovingStates = {
  component: QuestionWrapper,
  componentProps: {
    ...baseAddressComponentProps,
    movingFieldKey: 'movingStates' as const,
  },
  name: 'addressAndMovingStates',
  coreIf: true,
  schema: z.object({
    address: addressFieldSchema,
    movingStates: z.string().min(2, 'Please select one of the options above.'),
  }),
}

const buildStepFamily = (formResultsData: FormStepResult): StepFamily => {
  const movingValue = formResultsData.moving
  const addressData = formResultsData.address as Record<string, unknown> | undefined
  const isPostZipModal = movingValue && addressData?.zipcode
  const isMoving = (): boolean | undefined => {
    if (movingValue === 'yes') return true
    if (movingValue === 'no') return false
    return undefined
  }

  return createStepFamily(
    [
      {
        condition: () => Boolean(isPostZipModal && isMoving()),
        question: addressAndMovingStates,
        requiredFields: ['address', 'movingStates'],
        questionNames: ['addressAndMovingStates', 'homeInfo'],
      },
      {
        condition: () => Boolean(isPostZipModal && isMoving() === false),
        question: addressOnly,
        requiredFields: ['address'],
        questionNames: ['addressOnly', 'homeInfo'],
      },
    ],
    {
      question: addressAndMover,
      requiredFields: ['address', 'moving'],
      questionNames: ['addressAndMover', 'homeInfo'],
    },
  )
}

export const addressStepFamily = (formResultsData: FormStepResult = {}): StepFamily =>
  buildStepFamily(formResultsData)
